// ===== src/pages/admin/AdminUsers/index.jsx =====
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AdminTable from "../../../components/AdminTable";
import { API_DOMAIN } from "config";
import { formatDate } from "../../../utils/formatDate";

function roleLabel(row) {
  if (row?.provider_type === "private") return "מוכר פרטי";
  if (+row?.user_role === 2) return "ספק";
  if (+row?.user_role === 3) return "סוחר";
  return row?.user_role;
}

const emptyUser = (defaultRole = 3) => ({
  id: 0,
  name: "",
  contact_name: "",
  phone: "",
  email: "",
  user_role: defaultRole,
  provider_type: "company", // ✅ חדש
  status: 1,
});


export default function AdminUsers() {
  const { kind } = useParams(); // "providers" | "dealers"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [editUser, setEditUser] = useState(null);

  const title = useMemo(() => {
    if (kind === "providers") return "ניהול ספקים";
    if (kind === "dealers") return "ניהול סוחרים";
    if (kind === "privates") return "ניהול מוכרים פרטיים";

    return "ניהול משתמשים";
  }, [kind]);

  const defaultRole = useMemo(() => {
    if (kind === "providers") return 2;
    if (kind === "dealers") return 3;
    if (kind === "privates") return 2; // עדיין role=2

    return 3;
  }, [kind]);

  const subtitle = "הוספה, עריכה וניהול סטטוס משתמשים במערכת";

  const loadUsers = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_DOMAIN}/admin/getUsers?type=${kind}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErrorMsg("שגיאה בטעינת המשתמשים");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [kind]);

  const handleToggleStatus = async (user) => {
    const newStatus = user.status ? 0 : 1;
    try {
      const res = await fetch(`${API_DOMAIN}/admin/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!data.success) throw new Error("update failed");

      setRows((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, status: newStatus } : u))
      );
    } catch (e) {
      console.error(e);
      alert("שגיאה בעדכון סטטוס המשתמש");
    }
  };

  const openNewUser = () => {
    setEditUser(emptyUser(defaultRole));
    setErrorMsg("");
  };

  const openEditUser = (row) => {
    setEditUser({
      id: row.id,
      name: row.name || "",
contact_name: row.contactName || "", // ✅ במקום row.contact_name
      phone: row.phone || "",
      email: row.email || "",
      user_role: row.user_role ?? defaultRole,
      status: row.status ?? 1,
      provider_type: row.provider_type || "company",

    });
    setErrorMsg("");
  };

  const handleFieldChange = (field, value) => {
    setEditUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async () => {
    if (!editUser) return;
    setErrorMsg("");

const payload = {
  name: editUser.name.trim(),
  contact_name: (editUser.contact_name ?? "").trim(),
  phone: editUser.phone.trim(),
  email: editUser.email.trim(),
  user_role: +editUser.user_role || defaultRole,
  status: +editUser.status ? 1 : 0,
  provider_type: editUser.provider_type || "company", // ✅ תמיד
};


    if (!payload.name || !payload.phone) {
      setErrorMsg("שם ומספר נייד הם שדות חובה");
      return;
    }

    // אם אצלך בשרת contact_name הוא NOT NULL חובה:
    // אם תרצי להפוך לחובה גם בפופאפ admin:
    // אם לא - אפשר למחוק את התנאי הזה
    if (!payload.contact_name) {
      setErrorMsg("שם איש קשר הוא שדה חובה");
      return;
    }

    try {
      const url =
        editUser.id === 0
          ? `${API_DOMAIN}/admin/createUser`
          : `${API_DOMAIN}/admin/users/${editUser.id}`;

      const method = editUser.id === 0 ? "POST" : "PUT";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.success) {
        if (data.code === "PHONE_EXISTS") {
          setErrorMsg("מספר הטלפון כבר קיים במערכת");
        } else {
          setErrorMsg(data.error || "שמירת המשתמש נכשלה");
        }
        return;
      }

      await loadUsers();
      setEditUser(null);
    } catch (e) {
      console.error(e);
      setErrorMsg("שגיאה בשרת בעת שמירת המשתמש");
    }
  };

  // ==== עמודות הטבלה ==== //
  const columns = [
    {
      name: "",
      selector: (r) => r.id,
      width: "80px",
      sortable: true,
      center: true,
    },
    {
      name: "שם",
      selector: (r) => r.name,
      sortable: true,
      wrap: true,
    },
    {
      name: "שם איש קשר", // ✅ חדש
      selector: (r) => r.contactName,
      sortable: true,
      wrap: true,
      grow: 1.2,
    },
    {
      name: "טלפון",
      selector: (r) => r.phone,
      sortable: true,
      width: "140px",
    },
    {
      name: 'דוא"ל',
      selector: (r) => r.email,
      grow: 1.3,
      wrap: true,
    },
{
  name: "סוג משתמש",
  selector: (r) => r.user_role,
  sortable: true,
  width: "120px",
  cell: (r) => (
    <span className="inline-flex rounded-full bg-slate-100 border border-slate-300 px-2 py-0.5 text-[11px] text-slate-700">
      {roleLabel(r)}
    </span>
  ),
},

    {
      name: "תאריך הכנסה",
      selector: (r) => r.creation_date,
      sortable: true,
      width: "150px",
      cell: (r) => formatDate(r.creation_date),
    },
    {
      name: "סטטוס",
      selector: (r) => r.status,
      width: "160px",
      sortable: true,
      cell: (r) => (
        <div className="flex items-center justify-start">
          <button
            type="button"
            onClick={() => handleToggleStatus(r)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium min-w-[90px] justify-center
              ${
                r.status
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : "bg-rose-50 border-rose-300 text-rose-700"
              }
            `}
          >
            <span
              className={`h-2.5 w-2.5 rounded-full 
                ${r.status ? "bg-emerald-500" : "bg-rose-500"}
              `}
            />
            {r.status ? "פעיל" : "לא פעיל"}
          </button>
        </div>
      ),
    },
    {
      name: "פעולות",
      width: "120px",
      cell: (r) => (
        <button
          type="button"
          onClick={() => openEditUser(r)}
          className="text-xs rounded-full border border-slate-300 bg-white px-3 py-1 text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition"
        >
          עריכה
        </button>
      ),
    },
  ];

  const searchKeys = ["name", "contact_name", "phone", "email"]; // ✅ חדש
  const exportKeys = [
    "id",
    "name",
    "contact_name", // ✅ חדש
    "phone",
    "email",
    "user_role",
    "status",
    "creation_date",
  ];

  return (
    <div className="p-4 space-y-4 bg-slate-100 min-h-[calc(100vh-4rem)]" dir="rtl">
      {errorMsg && !editUser && (
        <div className="mb-2 rounded-lg bg-rose-50 border border-rose-300 px-3 py-2 text-sm text-rose-700">
          {errorMsg}
        </div>
      )}

      <AdminTable
        title={title}
        subtitle={subtitle}
        rows={rows}
        columns={columns}
        searchKeys={searchKeys}
        exportKeys={exportKeys}
        loading={loading}
        extraActions={
          <button
            type="button"
            onClick={openNewUser}
            style={{ color: "white" }}
            className="inline-flex items-center gap-2 rounded-full bg-slate-700 px-4 py-1.5 text-xs font-semibold text-white hover:bg-slate-600 transition"
          >
            <span className="text-sm">＋</span>
            משתמש חדש
          </button>
        }
      />

      {/* מודאל הוספה / עריכה */}
      {editUser && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            style={{ backgroundColor: "white" }}
            className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            dir="rtl"
          >
            {/* כותרת + איקס סגירה */}
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3 mb-4">
              <div className="space-y-1 text-right">
                <h2 className="text-lg font-semibold text-slate-900">
                  {editUser.id === 0 ? "הוספת משתמש חדש" : "עריכת משתמש"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditUser(null);
                  setErrorMsg("");
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition"
              >
                ×
              </button>
            </div>

            {/* הודעת שגיאה */}
            {errorMsg && (
              <div className="mb-3 rounded-xl bg-rose-50 border border-rose-300 px-3 py-2 text-xs text-rose-700">
                {errorMsg}
              </div>
            )}

            {/* גוף הטופס */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-right">
              {/* שם משתמש */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-700">
                  שם משתמש <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editUser.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>

              {/* ✅ חדש: שם איש קשר */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-700">
                  שם איש קשר <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editUser.contact_name}
                  onChange={(e) => handleFieldChange("contact_name", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>

              {/* טלפון */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-700">
                  נייד <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editUser.phone}
                  onChange={(e) => handleFieldChange("phone", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>

              {/* אימייל */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-700">
                  דוא"ל
                </label>
                <input
                  type="email"
                  value={editUser.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>

              {/* סוג משתמש */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-700">
                  סוג משתמש
                </label>

                <div className="relative">
               <select
  value={
    editUser.provider_type === "private"
      ? "private"
      : String(editUser.user_role)
  }
  onChange={(e) => {
    const v = e.target.value;

    if (v === "private") {
      handleFieldChange("user_role", 2);              // נשאר role=2
      handleFieldChange("provider_type", "private");  // ✅ ההבדל נשמר כאן
    } else {
      handleFieldChange("user_role", Number(v));
      handleFieldChange("provider_type", "company");  // או לאפס ל-null
    }
  }}
  className="user-role-select w-full rounded-xl ..."
>
  <option value="2">ספק</option>
  <option value="3">סוחר</option>
  <option value="private">מוכר פרטי</option>
</select>


                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                    ▼
                  </span>
                </div>
              </div>

              {/* סטטוס */}
              <div className="flex flex-col mt-1 md:col-span-2 items-start">
                <span className="text-xs font-medium text-slate-700 mb-1">
                  סטטוס
                </span>

                <div className="inline-flex rounded-full bg-slate-100 p-1 gap-1">
                  <button
                    type="button"
                    onClick={() => handleFieldChange("status", 1)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition min-w-[80px] ${
                      editUser.status
                        ? "bg-emerald-500 text-white shadow-sm"
                        : "text-slate-700"
                    }`}
                  >
                    פעיל
                  </button>

                  <button
                    type="button"
                    onClick={() => handleFieldChange("status", 0)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition min-w-[80px] ${
                      !editUser.status
                        ? "bg-rose-500 text-white shadow-sm"
                        : "text-slate-700"
                    }`}
                  >
                    לא פעיל
                  </button>
                </div>
              </div>
            </div>

            {/* כפתורים למטה */}
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleSaveUser}
                className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 transition"
              >
                שמירה
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditUser(null);
                  setErrorMsg("");
                }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-1.5 text-sm text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
